import React, { useState, useEffect } from 'react';
import { usePerspective } from '../contexts/PerspectiveContext';
import { logger } from '../utils/logger';
import { adminAPI } from '../services/api';
import PlaceOrderLipoderma from '../pages/client/PlaceOrderLipoderma';
import PlaceOrderNew from '../pages/client/PlaceOrderNew';

/**
 * PlaceOrderRouter
 * Determines which order page to show based on the effective user's organization.
 * When an admin is viewing as a client, it fetches the target client's organization
 * to show the correct order page (BARE -> Lipoderma, EPL -> equicenta CTM).
 */
const PlaceOrderRouter = ({ currentUser, onOrderSubmit }) => {
  const { isInSwitchedView, perspectiveContext } = usePerspective();
  const [effectiveOrganization, setEffectiveOrganization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const determineOrganization = async () => {
      setIsLoading(true);
      
      try {
        // If in switched view, fetch the target user's organization
        if (isInSwitchedView && perspectiveContext?.targetUserId) {
          logger.log('PlaceOrderRouter: Fetching target user organization for:', perspectiveContext.targetUserId);
          const response = await adminAPI.getUserById(perspectiveContext.targetUserId);
          
          if (response.success && response.data) {
            const targetUser = response.data;
            const org = targetUser.organization || targetUser.Organization;
            logger.log('PlaceOrderRouter: Target user organization:', org);
            setEffectiveOrganization(org);
          } else {
            logger.warn('PlaceOrderRouter: Failed to fetch target user, using currentUser organization');
            setEffectiveOrganization(currentUser?.organization);
          }
        } else {
          // Not in switched view, use current user's organization
          logger.log('PlaceOrderRouter: Using currentUser organization:', currentUser?.organization);
          setEffectiveOrganization(currentUser?.organization);
        }
      } catch (error) {
        logger.error('PlaceOrderRouter: Error determining organization:', error);
        // Fallback to current user's organization
        setEffectiveOrganization(currentUser?.organization);
      } finally {
        setIsLoading(false);
      }
    };

    determineOrganization();
  }, [isInSwitchedView, perspectiveContext?.targetUserId, currentUser?.organization]);

  // Show loading state while determining organization
  if (isLoading) {
    return <div style={{ padding: 16 }}>Loading...</div>;
  }

  // Route based on effective organization
  // BARE -> Lipoderma, EPL or anything else -> equicenta CTM
  if (effectiveOrganization === 'BARE') {
    return <PlaceOrderLipoderma currentUser={currentUser} onOrderSubmit={onOrderSubmit} />;
  }

  // Default to equicenta CTM for EPL or any other organization
  return <PlaceOrderNew currentUser={currentUser} onOrderSubmit={onOrderSubmit} />;
};

export default PlaceOrderRouter;
